import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertTriangle, RefreshCw, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OverWithdrawnSection = () => {
  const { getAuthHeaders } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [method, setMethod] = useState('CLAWBACK');
  const [collectionMethod, setCollectionMethod] = useState('offset_pending');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/data-health/over-withdrawn`, {
        headers: getAuthHeaders(),
      });
      setRows(res.data.rows || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openResolve = (row) => {
    setResolveTarget(row);
    setMethod('CLAWBACK');
    setCollectionMethod('offset_pending');
    setNotes('');
  };

  const handleResolve = async () => {
    if (!resolveTarget) return;
    if (method === 'PFS_ABSORBED' && notes.trim().length < 10) {
      toast.error('PFS-absorbed write-off requires notes (min 10 chars)');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/data-health/over-withdrawn/${resolveTarget.syndication_id}/resolve`,
        {
          method,
          collection_method: method === 'CLAWBACK' ? collectionMethod : undefined,
          notes: notes || null,
        },
        { headers: getAuthHeaders() },
      );
      toast.success(
        method === 'CLAWBACK'
          ? `Clawback posted for ${formatCurrency(resolveTarget.over_withdrawn_amount)}`
          : `PFS-absorbed loss recorded`,
      );
      setResolveTarget(null);
      fetchRows();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
        : detail || e.message || 'Resolution failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card data-testid="over-withdrawn-section">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <CardTitle className="text-base">
            Over-withdrawn syndicator positions ({rows.length})
          </CardTitle>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchRows}
          data-testid="over-withdrawn-refresh-btn"
        >
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="py-8 text-center text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">
            No over-withdrawn positions. All syndicator balances are healthy.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left">Syndicator</th>
                  <th className="px-4 py-2 text-left">Deal</th>
                  <th className="px-4 py-2 text-right">Running Collection</th>
                  <th className="px-4 py-2 text-right">Total Withdrawals</th>
                  <th className="px-4 py-2 text-right">Over-withdrawn</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr
                    key={r.syndication_id}
                    data-testid={`over-withdrawn-row-${r.syndication_id}`}
                  >
                    <td className="px-4 py-2 font-medium">{r.syndicator_name}</td>
                    <td className="px-4 py-2 font-mono text-xs">
                      <Link to={`/deals/${r.deal_id}`} className="text-blue-600 hover:underline">
                        #{r.deal_alias || r.deal_id_number || '—'}
                      </Link>
                      {r.merchant_name && (
                        <span className="text-gray-500 ml-2">{r.merchant_name}</span>
                      )}
                    </td>
                    <td
                      className={`px-4 py-2 text-right tabular-nums ${
                        r.running_collection < 0 ? 'text-red-600 font-semibold' : ''
                      }`}
                    >
                      {formatCurrency(r.running_collection)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {formatCurrency(r.total_withdrawals)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-red-700 font-bold">
                      {formatCurrency(r.over_withdrawn_amount)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        size="sm"
                        onClick={() => openResolve(r)}
                        className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-7"
                        data-testid={`over-withdrawn-resolve-btn-${r.syndication_id}`}
                      >
                        Resolve <ChevronRight className="h-3 w-3 ml-1" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Resolve Dialog */}
      <Dialog open={!!resolveTarget} onOpenChange={(v) => !v && setResolveTarget(null)}>
        <DialogContent data-testid="over-withdrawn-resolve-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Resolve Over-Withdrawn Position</DialogTitle>
          </DialogHeader>
          {resolveTarget && (
            <div className="space-y-4">
              <div className="rounded-md bg-gray-50 p-3 text-sm">
                <div>
                  <strong>{resolveTarget.syndicator_name}</strong> on Deal{' '}
                  <span className="font-mono">
                    #{resolveTarget.deal_alias || resolveTarget.deal_id_number}
                  </span>
                </div>
                <div className="text-red-700 font-bold tabular-nums mt-1">
                  Over-withdrawn: {formatCurrency(resolveTarget.over_withdrawn_amount)}
                </div>
              </div>

              <div>
                <Label>Resolution Method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="resolve-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CLAWBACK">
                      Post manual clawback (recover from syndicator)
                    </SelectItem>
                    <SelectItem value="PFS_ABSORBED">
                      Mark as PFS-absorbed (write off the loss)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {method === 'CLAWBACK' && (
                <div>
                  <Label>Collection Method</Label>
                  <Select value={collectionMethod} onValueChange={setCollectionMethod}>
                    <SelectTrigger data-testid="resolve-collection-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="offset_pending">Apply to next distributions</SelectItem>
                      <SelectItem value="direct">Collect directly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>
                  Notes
                  {method === 'PFS_ABSORBED' && <span className="text-red-500"> *</span>}
                </Label>
                <Textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={
                    method === 'PFS_ABSORBED'
                      ? 'Required: explain why PFS is absorbing this loss (min 10 chars)'
                      : 'Optional context'
                  }
                  data-testid="resolve-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResolveTarget(null)}
              disabled={submitting}
              data-testid="resolve-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResolve}
              disabled={submitting}
              className={
                method === 'PFS_ABSORBED'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-amber-600 hover:bg-amber-700 text-white'
              }
              data-testid="resolve-confirm-btn"
            >
              {submitting ? 'Posting…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default OverWithdrawnSection;
